import type { Locale as AntdLocale } from 'antd/es/locale';
import { getMessages } from './component-messages';
import type { LocaleKey } from './context';
import {
  accountAccessEnUS,
  accountAccessZhCN,
  accountAccessZhHK,
} from './locales/account-access';
import enLoginLog from './locales/account-login-log-en-US';
import zhCNLoginLog from './locales/account-login-log-zh-CN';
import zhHKLoginLog from './locales/account-login-log-zh-HK';
import {
  businessPresetsEnUS,
  businessPresetsZhCN,
  businessPresetsZhHK,
} from './locales/business-presets';
import {
  workbenchEnUS,
  workbenchZhCN,
  workbenchZhHK,
} from './locales/workbench';

export interface LocaleBundle {
  key: LocaleKey;
  messages: Record<string, string>;
  antd: AntdLocale;
}

/**
 * 固定界面词条和组件语言资源一起发布，切语时作为一份资源加载。
 *
 * 主语言包外面还挂着几份按域拆出来的小词条（`account-login-log-*` 这一族）：
 * 主包是三个语言一起改的热点，几千行的文件里新加一组词条很容易和别人的改动撞行。
 * 合并时主包**放在后面**——同名键以主包为准，拆分出来的那份只补它没有的键。
 */
const LOADERS: Record<LocaleKey, () => Promise<LocaleBundle>> = {
  'zh-CN': async () => {
    const [messages, antd] = await Promise.all([
      import('./locales/zh-CN'),
      import('antd/locale/zh_CN'),
      import('dayjs/locale/zh-cn'),
    ]);
    return {
      key: 'zh-CN',
      messages: {
        ...getMessages('zh-CN'),
        ...zhCNLoginLog,
        ...messages.default,
        ...accountAccessZhCN,
        ...businessPresetsZhCN,
        ...workbenchZhCN,
      },
      antd: antd.default,
    };
  },
  'zh-HK': async () => {
    const [messages, antd] = await Promise.all([
      import('./locales/zh-HK'),
      import('antd/locale/zh_HK'),
      import('dayjs/locale/zh-hk'),
    ]);
    return {
      key: 'zh-HK',
      messages: {
        ...getMessages('zh-HK'),
        ...zhHKLoginLog,
        ...messages.default,
        ...accountAccessZhHK,
        ...businessPresetsZhHK,
        ...workbenchZhHK,
      },
      antd: antd.default,
    };
  },
  'en-US': async () => {
    const [messages, antd] = await Promise.all([
      import('./locales/en-US'),
      import('antd/locale/en_US'),
    ]);
    return {
      key: 'en-US',
      messages: {
        ...getMessages('en-US'),
        ...enLoginLog,
        ...messages.default,
        ...accountAccessEnUS,
        ...businessPresetsEnUS,
        ...workbenchEnUS,
      },
      antd: antd.default,
    };
  },
};

export function loadLocaleResources(locale: LocaleKey): Promise<LocaleBundle> {
  return LOADERS[locale]();
}

export function reloadLocaleResources(): void {
  // 失败的动态模块可能被当前文档缓存，刷新后才能可靠地重新获取。
  window.location.reload();
}
